/**
 * Fait : fixe la liste canonique des collections MongoDB du produit —
 *   celles du plan, ni plus ni moins. Les migrations, les index et le registre
 *   des suppressions s'appuient sur cette liste, jamais sur des chaînes
 *   écrites à la main.
 *
 * Ne fait pas : il ne décrit pas la forme des documents — chaque famille de
 *   collections a son module — et ne crée rien en base : créer est le métier
 *   des migrations (phase 2, lot 2.3).
 */

/**
 * Les collections du produit, dans l'ordre du plan (phase 2, « Définir les
 * collections »). Un nom qui n'est pas ici n'existe pas ; en ajouter un est
 * une décision de plan, pas un geste de code.
 *
 * `reinitialisations` est née du chantier « administration des comptes » le
 * 2026-08-06 : un mot de passe oublié se répare par un lien à usage unique,
 * qui a besoin de son propre magasin.
 *
 * `verrous` est née le 2026-08-07, sur décision de Charles : « quand il y a un
 * risque de concurrence, on met toujours un verrou ». Elle ne contient qu'UN
 * document, et ne porte aucune donnée du produit — seulement de quoi faire
 * s'entrechoquer deux écritures que MongoDB, sans elle, ne verrait pas se
 * croiser.
 */
export const NOMS_DES_COLLECTIONS = [
  'comptes',
  'invitations',
  'sessions',
  'projets',
  'membresDeProjet',
  'conversations',
  'branches',
  'messages',
  'prompts',
  'fichiersDeProjet',
  'catalogueDeModeles',
  'budgets',
  'reservationsDUsage',
  'marqueursDeSuppression',
  'reinitialisations',
  'verrous',
  'versionsDeMigration',
] as const;

export type NomDeCollection = (typeof NOMS_DES_COLLECTIONS)[number];

/**
 * Les collections soumises à la LOI du critère 7 : leurs documents portent
 * OBLIGATOIREMENT un `projetId`. Les schémas le garantissent côté code, et la
 * migration pose sur CES collections un validateur MongoDB — la base
 * elle-même refuse un document hors projet, même inséré en contournant le
 * code. Un test confronte cette liste aux schémas : elle ne peut pas dériver.
 */
export const COLLECTIONS_RATTACHEES_AU_PROJET = [
  'membresDeProjet',
  'conversations',
  'branches',
  'messages',
  'fichiersDeProjet',
  'reservationsDUsage',
] as const satisfies readonly NomDeCollection[];

/** Dit si une valeur est un nom de collection du produit. */
export function estUnNomDeCollection(valeur: unknown): valeur is NomDeCollection {
  return typeof valeur === 'string' && (NOMS_DES_COLLECTIONS as readonly string[]).includes(valeur);
}
